import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Spinner from '../shared/spinner.component';

import { IsClientLocked } from '../shared/clientLocked/IsClientLocked.component';
import { debouncedRebuildReactTooltip } from '../shared/debouncedRebuildReactTooltip';
import { clientLockedMessages } from '../shared/clientLocked/messages';

import config from '../config';
import { changeCursorTo, CursorTypes } from '../utils/changeCursor';

import DropdownMenu from '../shared/dropdownMenu';
import { assignmentPermissions, withAssignments, assignmentTypes } from '../shared/authorization';
import { downloadTrialBalanceTemplate } from './trialBalanceTemplate';
import {
  hasAccessSelector,
  canCalculateProformaProvisionSelector,
} from './store/selectors';

import { generateAssetPath } from '../utils/assetsHelpers';

import UploadTrialBalanceButton from './uploadTrialBalanceButton.component';
import { isInCurrentPeriodSelector } from '../shared/store/selectors';

class HamburgerDropdown extends PureComponent {
  static propTypes = {
    calculateProvision: PropTypes.func.isRequired,
    companySource: PropTypes.string,
    uploadTrialBalanceFile: PropTypes.func.isRequired,
    isCalculatingProvision: PropTypes.bool.isRequired,
    isUploadingTrialBalance: PropTypes.bool.isRequired,
    onCalculateProvisionFinish: PropTypes.func.isRequired,
    onTrialBalanceUploadFinish: PropTypes.func.isRequired,
    cannotDownloadCompanyDetails: PropTypes.bool.isRequired,
    downloadProvisionDetail: PropTypes.func.isRequired,
    cannotUploadTrialBalance: PropTypes.bool.isRequired,
    showCannotUploadMessageModal: PropTypes.func.isRequired,
    cannotCalculateTrialBalance: PropTypes.bool.isRequired,
    showCannotCalculateMessageModal: PropTypes.func.isRequired,
    period: PropTypes.shape({ name: PropTypes.string.isRequired }).isRequired,
    companyCode: PropTypes.string.isRequired,
    functionalCurrency: PropTypes.string,
    hasAccess: PropTypes.bool.isRequired,
    proformaCalc: PropTypes.func.isRequired,
    isProformaCalc: PropTypes.bool.isRequired,
    environmentSuffix: PropTypes.string.isRequired,
    canCalculateProformaProvision: PropTypes.bool.isRequired,
    isDownloadingProvisionDetails: PropTypes.bool.isRequired,
    isInCurrentPeriod: PropTypes.bool.isRequired,
  };

  componentDidUpdate(prevProps) {
    const isCalculatingProvisionChanged = prevProps.isCalculatingProvision !== this.props.isCalculatingProvision;
    if (isCalculatingProvisionChanged && !this.props.isCalculatingProvision) {
      this.hideDropDown();
      this.props.onCalculateProvisionFinish();
      changeCursorTo(CursorTypes.DEFAULT);
    }

    const isProformaCalcChanged = prevProps.isProformaCalc !== this.props.isProformaCalc;
    if (isProformaCalcChanged && !this.props.isProformaCalc) {
      this.hideDropDown();
      this.props.onCalculateProvisionFinish();
      changeCursorTo(CursorTypes.DEFAULT);
    }

    const isUploadingTrialBalanceChanged = prevProps.isUploadingTrialBalance !== this.props.isUploadingTrialBalance;
    if (isUploadingTrialBalanceChanged && !this.props.isUploadingTrialBalance) {
      this.hideDropDown();
      this.props.onTrialBalanceUploadFinish();
      changeCursorTo(CursorTypes.DEFAULT);
    }
  }

  hideDropDown = () => {
    this.dropdown.hideDropDown();
  };

  calculateProvision = () => {
    const { cannotCalculateTrialBalance, calculateProvision, showCannotCalculateMessageModal } = this.props;
    if (cannotCalculateTrialBalance) {
      showCannotCalculateMessageModal();
    } else {
      calculateProvision();
      changeCursorTo(CursorTypes.WAIT);
    }
  };

  proformaCalc = () => {
    const { proformaCalc } = this.props;
    proformaCalc();
    changeCursorTo(CursorTypes.WAIT);
  };

  downloadProvisionDetails = () => {
    this.props.downloadProvisionDetail();
  };

  downloadTrialBalanceTemplate = () => {
    const {
      companySource,
      period,
      companyCode,
      functionalCurrency,
      environmentSuffix,
    } = this.props;

    downloadTrialBalanceTemplate(
      {
        companySource,
        period: period.name,
        companyCode,
        functionalCurrency,
        environmentSuffix,
      }
    );
  };

  uploadTrialBalanceFile = event => {
    const file = event.target.files[0];
    this.props.uploadTrialBalanceFile(file);
    event.target.value = '';
    changeCursorTo(CursorTypes.WAIT);
  };

  renderCalculateProvisionButton = () => {
    const {
      isCalculatingProvision,
      hasAccess,
      companyCode,
    } = this.props;

    if (!hasAccess) {
      return null;
    }

    return (
      <button
        className="hamburger-action link-button"
        type="button"
        disabled={!companyCode}
        onClick={this.calculateProvision}
      >
        {isCalculatingProvision ? (
          <span>
            <Spinner small /> Calculating
          </span>
        ) : 'Calculate Provision'}
      </button>
    );
  };

  renderProformaCalcButton = () => {
    const {
      isProformaCalc,
      canCalculateProformaProvision,
      companyCode,
      isInCurrentPeriod,
    } = this.props;

    if (!canCalculateProformaProvision || !isInCurrentPeriod) {
      return null;
    }

    return (
      <IsClientLocked
        onChange={debouncedRebuildReactTooltip}
        render={isClientLocked => ( // eslint-disable-line react/jsx-no-bind
          <div data-tip={isClientLocked ? clientLockedMessages.PROFORMA_CALC : ''}>
            <button
              disabled={isClientLocked || !companyCode}
              className="hamburger-action link-button sub-nav-link"
              type="button"
              onClick={this.proformaCalc}
            >
              {isProformaCalc ? (
                <span>
                  <Spinner small /> Calculating
                </span>
              ) : 'Proforma Calculation'}
            </button>
          </div>
        )}
      />
    );
  };

  renderDownloadProvisionDetails = () => {
    const {
      cannotDownloadCompanyDetails,
      isDownloadingProvisionDetails,
      companyCode,
    } = this.props;

    if (cannotDownloadCompanyDetails) {
      return null;
    }

    return (
      <button
        className="hamburger-action link-button"
        tabIndex="0"
        disabled={!companyCode}
        onClick={this.downloadProvisionDetails}
      >
        {isDownloadingProvisionDetails
          ? (
            <span>
              <Spinner small /> Exporting
            </span>
          ) : 'Download Provision Detail'}
      </button>
    );
  };

  render() {
    const {
      cannotUploadTrialBalance,
      showCannotUploadMessageModal,
      hasAccess,
      companyCode,
    } = this.props;

    return (
      <DropdownMenu ref={dropdown => this.dropdown = dropdown} contentClassName="hamburger-menu">
        {this.renderCalculateProvisionButton()}
        {this.renderProformaCalcButton()}
        {this.renderDownloadProvisionDetails()}

        <button
          className="hamburger-action link-button"
          disabled={!generateAssetPath(config.templates.TRIAL_BALANCE) || !companyCode}
          onClick={this.downloadTrialBalanceTemplate}
        >
          Download Trial Balance Template
        </button>

        <UploadTrialBalanceButton
          cannotUploadTrialBalance={cannotUploadTrialBalance}
          showCannotUploadMessageModal={showCannotUploadMessageModal}
          hasAccess={hasAccess}
          uploadTrialBalanceFile={this.uploadTrialBalanceFile}
          companyCode={companyCode}
        />
      </DropdownMenu>
    );
  }
}

export default withAssignments([
  { assignmentType: assignmentTypes.CC, requiredPermission: assignmentPermissions.Modify },
])(
  connect((state, ownProps) => ({
    hasAccess: hasAccessSelector(state, ownProps.hasAccessBasedOnAssignments),
    canCalculateProformaProvision: canCalculateProformaProvisionSelector(state),
    isInCurrentPeriod: isInCurrentPeriodSelector(state),
  }))(HamburgerDropdown)
);
